import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Pill, ArrowRight } from "lucide-react";

const TEAL = "#0D9488";
const TEAL_D = "#0F766E";

/** Compact dashboard tile — today's supplement completion percentage. */
export default function SupplementCompletionCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data: d } = await api.get("/supplements/today/summary");
        if (!cancel) setData(d);
      } catch {
        if (!cancel) setData({ total: 0, completed: 0, pct: 0 });
      }
    })();
    return () => { cancel = true; };
  }, []);

  if (!data) {
    return <div className="h-full min-h-[160px] bg-white border border-slate-200 rounded-xl animate-pulse" />;
  }

  const empty = data.total === 0;
  const done = data.pct === 100;

  return (
    <Link
      to="/supplements"
      className="group block bg-white border border-slate-200 rounded-xl p-5 hover:border-teal-300 hover:shadow-sm transition-all"
      data-testid="supp-dash-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Pill className="w-4 h-4" style={{ color: TEAL }} />
          <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: TEAL }}>Supplements</div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-teal-600 group-hover:translate-x-0.5 transition-all" />
      </div>

      {empty ? (
        <div>
          <div className="font-display font-bold text-slate-900 text-2xl leading-tight">Add your stack</div>
          <div className="text-xs text-slate-500 mt-1">Describe your daily supplements to start tracking.</div>
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-1.5" data-testid="supp-dash-pct">
            <span className="font-display font-bold text-slate-900 text-4xl tabular-nums">{data.pct}</span>
            <span className="text-lg text-slate-500 font-semibold">%</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5 tabular-nums">
            {data.completed} / {data.total} taken today
          </div>
          <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full transition-all duration-500 ease-out"
              style={{ width: `${data.pct}%`, background: `linear-gradient(90deg, ${TEAL}, ${TEAL_D})` }}
              data-testid="supp-dash-bar"
            />
          </div>
          {done && (
            <div className="mt-2 text-[11px] font-semibold" style={{ color: TEAL }}>
              ✓ Stack complete for today
            </div>
          )}
        </>
      )}
    </Link>
  );
}
